import { useState, useRef, useEffect } from "react";
import { Button, Input } from "@headerhawk/ui";
import {
  validateSiteForm,
  normalizeDomain,
  hasErrors,
  type SiteFormData,
  type SiteValidationErrors,
} from "@/lib/validation/site";

interface SiteFormProps {
  onSubmit: (data: SiteFormData) => Promise<void>;
  isSubmitting: boolean;
  onBack?: () => void;
  submitLabel?: string;
  defaultValues?: Partial<SiteFormData>;
  disabled?: boolean;
}

export function SiteForm({
  onSubmit,
  isSubmitting,
  onBack,
  submitLabel = "Create Site",
  defaultValues,
  disabled = false,
}: SiteFormProps) {
  const [name, setName] = useState(defaultValues?.name ?? "");
  const [domain, setDomain] = useState(defaultValues?.domain ?? "");
  const [errors, setErrors] = useState<SiteValidationErrors>({});
  const [touched, setTouched] = useState<{ name?: boolean; domain?: boolean }>(
    {},
  );

  const nameInputRef = useRef<HTMLInputElement>(null);
  const domainInputRef = useRef<HTMLInputElement>(null);

  // Focus first input on mount (if not disabled)
  useEffect(() => {
    if (!disabled && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [disabled]);

  const validateField = (field: "name" | "domain", value: string) => {
    const formData: SiteFormData = {
      name: field === "name" ? value : name,
      domain: field === "domain" ? value : domain,
    };
    const fieldErrors = validateSiteForm(formData);
    setErrors((prev) => ({
      ...prev,
      [field]: fieldErrors[field],
    }));
  };

  const handleBlur = (field: "name" | "domain") => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, field === "name" ? name : domain);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedDomain = normalizeDomain(domain);
    const formData: SiteFormData = {
      name: name.trim(),
      domain: normalizedDomain,
    };

    const validationErrors = validateSiteForm(formData);
    setErrors(validationErrors);
    setTouched({ name: true, domain: true });

    if (hasErrors(validationErrors)) {
      // Focus the first field with an error
      if (validationErrors.name && nameInputRef.current) {
        nameInputRef.current.focus();
      } else if (validationErrors.domain && domainInputRef.current) {
        domainInputRef.current.focus();
      }
      return;
    }

    await onSubmit(formData);
  };

  const showNameError = touched.name && errors.name;
  const showDomainError = touched.domain && errors.domain;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <label
          htmlFor="site-name"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          Site name
        </label>
        <Input
          ref={nameInputRef}
          id="site-name"
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (touched.name) {
              validateField("name", e.target.value);
            }
          }}
          onBlur={() => handleBlur("name")}
          placeholder="My Website"
          maxLength={100}
          disabled={isSubmitting || disabled}
          aria-describedby={showNameError ? "name-error" : "name-hint"}
          aria-invalid={showNameError ? true : undefined}
        />
        {showNameError ? (
          <p id="name-error" className="text-sm text-destructive">
            {errors.name}
          </p>
        ) : (
          <p id="name-hint" className="text-sm text-muted-foreground">
            A friendly name to identify this site
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label
          htmlFor="site-domain"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          Domain
        </label>
        <Input
          ref={domainInputRef}
          id="site-domain"
          type="text"
          value={domain}
          onChange={(e) => {
            setDomain(e.target.value);
            if (touched.domain) {
              validateField("domain", e.target.value);
            }
          }}
          onBlur={() => handleBlur("domain")}
          placeholder="example.com"
          disabled={isSubmitting || disabled}
          aria-describedby={showDomainError ? "domain-error" : "domain-hint"}
          aria-invalid={showDomainError ? true : undefined}
        />
        {showDomainError ? (
          <p id="domain-error" className="text-sm text-destructive">
            {errors.domain}
          </p>
        ) : (
          <p id="domain-hint" className="text-sm text-muted-foreground">
            The domain where you'll add CSP headers (e.g., example.com)
          </p>
        )}
      </div>

      <div className="flex justify-between pt-4">
        {onBack && (
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={isSubmitting}
          >
            Back
          </Button>
        )}
        <Button
          type="submit"
          disabled={isSubmitting || disabled}
          className={!onBack ? "ml-auto" : ""}
        >
          {isSubmitting ? "Creating..." : submitLabel}
        </Button>
      </div>
    </form>
  );
}
